import { observer } from 'mobx-react-lite';
import { type ReactNode } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { Button, type ButtonProps } from '@/components/ui';
import { XStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';
import { formatDateLocale } from '@/utils/date';

import { AddressChooser } from './address-chooser';
import {
  addressesToLookUp,
  buttonLayout,
  connectionActions,
  formatDays,
  inviteWaitDays,
  STATE_LABELS,
  type ActionSubject,
  type AddressEntry,
  type AddressState,
} from './connection-actions';
import {
  useLittlebirdAccountsQuery,
  type ConnectionSurface,
} from './connections.query';
import { pendingDeclines } from './pending-declines.store';

type Face = {
  variant: NonNullable<ButtonProps['variant']>;
  icon?: ReactNode;
};

// Connect is filled and carries the Littlebird mark, and Invite is an outline with an envelope, so that the two never
// look alike: one asks someone who is on Littlebird, and the other emails someone who is not.
const FACES: Record<AddressState, Face> = {
  accept: { variant: 'default' },
  connect: {
    variant: 'default',
    icon: <AppLogo size={14} customColor="currentColor" decorative />,
  },
  requested: { variant: 'secondary' },
  connected: { variant: 'secondary' },
  invite: { variant: 'outline', icon: <Icons.mail /> },
  inviteAgain: { variant: 'outline', icon: <Icons.mail /> },
  invited: { variant: 'secondary' },
};

const inviteTooltip = ({ account }: AddressEntry): string | undefined => {
  const waitDays = account && inviteWaitDays(account);
  if (!account?.sentAt || !waitDays) return undefined;
  return `Invitation sent on ${formatDateLocale(account.sentAt)}. You can invite again in ${formatDays(waitDays)}.`;
};

// The trigger of a menu shades itself grey while the menu is open, which would turn a filled button into a quiet one
const FILLED_WHEN_OPEN =
  'data-[state=open]:bg-accent-hover data-[state=open]:text-accent-foreground';

// The thin line between the two parts of a split button. An outline has its border for it.
const dividerClassName = (variant: Face['variant']) =>
  variant === 'outline'
    ? 'border-l-0'
    : 'relative before:absolute before:inset-y-1.5 before:left-0 before:w-px before:bg-current before:opacity-25';

/** One button for the state that matters most for the subject: Accept, Connect, Requested, Connected, Invite or
 * Invited. With several addresses it is a split button: the main part acts on the top address, and a chevron beside it
 * opens a menu with a row for each address. A request that came in is answered with Accept and a Decline, which can be
 * undone, and the menu waits. A request that waits in `pendingDeclines` is hidden, whichever screen declined it. `click`
 * carries out what the state of an address calls for. Only a person can have a request to answer, so `decline` is for a
 * person alone. In the share picker the buttons are small. */
export const ConnectionControls = observer(
  ({
    subject,
    click,
    isPending,
    surface,
    decline,
  }: {
    subject: ActionSubject;
    click: (entry: AddressEntry) => Promise<boolean>;
    isPending: boolean;
    surface: ConnectionSurface;
    decline?: (entry: AddressEntry) => void;
  }) => {
    const { data: lookup } = useLittlebirdAccountsQuery(
      addressesToLookUp(subject)
    );
    const entries = connectionActions(subject, lookup?.emails ?? null).filter(
      ({ account }) => !account || !pendingDeclines.has(account.accountId)
    );
    const layout = buttonLayout(entries);
    if (!layout) return null;

    const { main, hasAddressList, hasDecline } = layout;
    const isCompact = surface === 'share_picker';
    const size = isCompact ? 'xs' : 'sm';
    const face = FACES[main.state];
    const isWaiting = main.state === 'invited';
    const requestFrom = `the request from ${main.email}`;
    // The label names the address that the main part acts on, where the person has several
    let mainLabel: string | undefined;
    if (hasDecline) mainLabel = `Accept ${requestFrom}`;
    else if (hasAddressList)
      mainLabel = `${STATE_LABELS[main.state]} ${main.email}`;

    const mainButton = (
      <Button
        variant={face.variant}
        size={size}
        isLoading={isPending}
        aria-label={mainLabel}
        // An invite that waits is shown but does not respond, and it still answers the hover with its tooltip
        aria-disabled={isWaiting || undefined}
        className={cn(
          hasAddressList && 'rounded-r-none',
          isWaiting && 'cursor-default opacity-60'
        )}
        tooltip={isWaiting ? inviteTooltip(main) : undefined}
        onClick={() => void click(main)}
      >
        {face.icon}
        {STATE_LABELS[main.state]}
      </Button>
    );

    return (
      <XStack className="min-w-0 items-center gap-1">
        {/* The main part stays where it is when the list of the addresses joins it, so that a click on it is not lost */}
        <XStack>
          {mainButton}
          {hasAddressList && (
            <AddressChooser entries={entries} onChoose={click}>
              <Button
                variant={face.variant}
                size={size}
                aria-label="Choose an address"
                disabled={isPending}
                className={cn(
                  isCompact ? 'w-6' : 'w-7',
                  'rounded-l-none px-0',
                  dividerClassName(face.variant),
                  face.variant === 'default' && FILLED_WHEN_OPEN
                )}
              >
                <Icons.chevronDown />
              </Button>
            </AddressChooser>
          )}
        </XStack>
        {hasDecline && (
          <Button
            variant="ghost"
            size={size}
            disabled={isPending}
            aria-label={`Decline ${requestFrom}`}
            onClick={() => decline?.(main)}
          >
            Decline
          </Button>
        )}
      </XStack>
    );
  }
);
